import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Search, Layers, Image, Star,
  Menu, X,
  Beaker, Sparkles, Clock, Zap, Palette, Camera, Users, Sun,
  Film, PenTool, Move, Clapperboard, Wrench, Send, RotateCw, Heart
} from 'lucide-react';
import { toast } from '../utils/toast';
import RecipeAssistant from '../components/RecipeAssistant';
import { GlobalSidebar } from '../components/GlobalSidebar';

const API = window.__API_BASE;

const CATEGORY_ICONS = {
  'Visual Style & Color': Palette,
  'Shot Composition': Camera,
  'Character & Casting': Users,
  'Lighting & Atmosphere': Sun,
  'VFX & Post': Film,
  'Storyboarding': PenTool,
  'Motion & Video': Move,
  'Production Tools': Wrench,
};

const CATEGORY_COLORS = {
  'Visual Style & Color': { bg: 'rgba(168, 85, 247, 0.15)', border: 'rgba(168, 85, 247, 0.3)', text: '#A855F7' },
  'Shot Composition': { bg: 'rgba(14, 165, 233, 0.15)', border: 'rgba(14, 165, 233, 0.3)', text: '#0EA5E9' },
  'Character & Casting': { bg: 'rgba(236, 72, 153, 0.15)', border: 'rgba(236, 72, 153, 0.3)', text: '#EC4899' },
  'Lighting & Atmosphere': { bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.3)', text: '#F59E0B' },
  'VFX & Post': { bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.3)', text: '#10B981' },
  'Storyboarding': { bg: 'rgba(99, 102, 241, 0.15)', border: 'rgba(99, 102, 241, 0.3)', text: '#6366F1' },
  'Motion & Video': { bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.3)', text: '#EF4444' },
  'Production Tools': { bg: 'rgba(156, 163, 175, 0.15)', border: 'rgba(156, 163, 175, 0.3)', text: '#9CA3AF' },
};

const TIER_CONFIG = {
  'Simple': { label: 'Quick', color: '#22C55E', icon: Zap },
  'Medium': { label: 'Standard', color: '#0EA5E9', icon: Sparkles },
  'Complex': { label: 'Advanced', color: '#F59E0B', icon: Clock },
};

export default function RecipesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestText, setRequestText] = useState('');
  const [requestEmail, setRequestEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [runSummary, setRunSummary] = useState({});
  const [favorites, setFavorites] = useState(new Set());

  useEffect(() => {
    loadRecipes();
    loadCategories();
    loadRunSummary();
    loadFavorites();
    document.title = 'Recipes — Eipicenter';

    // Re-fetch when the tab regains focus or browser window is focused —
    // this ensures the recipe count/list stays in sync when a recipe is
    // added or removed on the backend without forcing a hard refresh.
    const refetch = () => {
      if (document.visibilityState === 'visible') {
        loadRecipes();
        loadCategories();
      }
    };
    window.addEventListener('focus', refetch);
    document.addEventListener('visibilitychange', refetch);

    return () => {
      document.title = 'Eipicenter';
      window.removeEventListener('focus', refetch);
      document.removeEventListener('visibilitychange', refetch);
    };
  }, []);

  const loadRecipes = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/recipes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipes(data.recipes || []);
    } catch (err) {
      console.error('Failed to load recipes:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/recipes/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadRunSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/recipes/user/run-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRunSummary(data.run_summary || {});
    } catch (err) {
      // Non-critical — Quick Run just won't show
    }
  };

  const loadFavorites = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/recipes/user/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(new Set(data.favorites || []));
    } catch (err) {
      // Non-critical
    }
  };

  const toggleFavorite = async (slug) => {
    const token = localStorage.getItem('token');
    try {
      const { data } = await axios.post(`${API}/api/recipes/${slug}/favorite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(prev => {
        const next = new Set(prev);
        if (data.favorited) { next.add(slug); } else { next.delete(slug); }
        return next;
      });
      toast.success(data.favorited ? 'Added to favorites' : 'Removed from favorites');
    } catch (err) {
      toast.error('Failed to update favorite');
    }
  };

  const submitRequest = async () => {
    if (!requestText.trim()) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/recipes/request`, {
        description: requestText,
        email: requestEmail || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Recipe request submitted!');
      setShowRequestModal(false);
      setRequestText('');
      setRequestEmail('');
    } catch (err) {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredRecipes = useMemo(() => {
    let result = recipes;
    if (activeCategory === '__favorites__') {
      result = result.filter(r => favorites.has(r.slug));
    } else if (activeCategory) {
      result = result.filter(r => r.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.category.toLowerCase().includes(q)
      );
    }
    return result;
  }, [recipes, activeCategory, searchQuery, favorites]);

  const featuredRecipes = useMemo(() => recipes.filter(r => r.featured), [recipes]);

  const groupedByCategory = useMemo(() => {
    if (activeCategory === '__favorites__') {
      return { 'Favorites': filteredRecipes };
    }
    const groups = {};
    filteredRecipes.forEach(r => {
      if (!groups[r.category]) groups[r.category] = [];
      groups[r.category].push(r);
    });
    return groups;
  }, [filteredRecipes, activeCategory]);

  return (
    <div className="h-screen flex bg-[#0B0F19] text-white overflow-hidden" data-testid="recipes-page">
      {/* Sidebar — canonical app-wide left panel */}
      <GlobalSidebar active="recipes" recipeCount={recipes.length} sidebarOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
        {/* Header */}
        <header className="h-14 px-4 flex items-center justify-between border-b border-white/[0.06] flex-shrink-0"
          style={{ background: 'rgba(11, 15, 25, 0.8)', backdropFilter: 'blur(20px)' }}>
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="p-2 hover:bg-white/5 rounded-lg lg:hidden">
              <Menu className="w-5 h-5 text-white/60" />
            </button>
            <Beaker className="w-5 h-5 text-[#0EA5E9]" />
            <h1 className="text-sm font-semibold text-white">Recipes</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/recipes/compare')}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-[#0EA5E9] bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 hover:bg-[#0EA5E9]/20 transition-all"
              data-testid="compare-recipes-btn"
            >
              <Layers className="w-3.5 h-3.5 inline mr-1" />Compare
            </button>
            <span className="text-xs text-white/40 hidden sm:inline">{recipes.length} recipes available</span>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* Hero Section */}
          <div className="px-6 pt-8 pb-6" style={{ background: 'linear-gradient(180deg, rgba(14, 165, 233, 0.05) 0%, transparent 100%)' }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2" data-testid="recipes-title">Recipes</h2>
            <p className="text-sm text-white/50 mb-6 max-w-xl">Creative workflows built by filmmakers, for filmmakers. One-click tools for color grading, shot composition, VFX, storyboarding, and more.</p>

            {/* Search */}
            <div className="relative max-w-lg">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                type="text"
                placeholder="Search recipes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm bg-white/[0.05] border border-white/[0.08] text-white placeholder-white/30 focus:outline-none focus:border-[#0EA5E9]/40 transition-colors"
                data-testid="recipe-search"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X className="w-4 h-4 text-white/40" />
                </button>
              )}
            </div>
          </div>

          {/* Category Filter Pills */}
          <div className="px-6 pb-4 relative">
            <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 pr-8">
              <button
                onClick={() => setActiveCategory(null)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                  !activeCategory ? 'bg-[#0EA5E9]/20 text-[#0EA5E9] border border-[#0EA5E9]/30' : 'bg-white/[0.04] text-white/50 border border-white/[0.06] hover:bg-white/[0.08]'
                }`}
                data-testid="category-all"
              >
                All Recipes
              </button>
              {favorites.size > 0 && (
                <button
                  onClick={() => setActiveCategory(activeCategory === '__favorites__' ? null : '__favorites__')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 flex items-center gap-1.5 ${
                    activeCategory === '__favorites__'
                      ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                      : 'bg-white/[0.04] text-white/50 border border-white/[0.06] hover:bg-white/[0.08]'
                  }`}
                  data-testid="category-favorites"
                >
                  <Heart className={`w-3 h-3 ${activeCategory === '__favorites__' ? 'fill-rose-400' : ''}`} />
                  Favorites ({favorites.size})
                </button>
              )}
            {categories.map(cat => {
              const colors = CATEGORY_COLORS[cat.name] || CATEGORY_COLORS['Production Tools'];
              const Icon = CATEGORY_ICONS[cat.name] || Wrench;
              return (
                <button
                  key={cat.name}
                  onClick={() => setActiveCategory(activeCategory === cat.name ? null : cat.name)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1.5 flex-shrink-0"
                  style={{
                    background: activeCategory === cat.name ? colors.bg : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${activeCategory === cat.name ? colors.border : 'rgba(255,255,255,0.06)'}`,
                    color: activeCategory === cat.name ? colors.text : 'rgba(255,255,255,0.5)',
                  }}
                  data-testid={`category-${cat.name}`}
                >
                  <Icon className="w-3 h-3" />
                  {cat.name} ({cat.count})
                </button>
              );
            })}
            </div>
            {/* Scroll fade indicator */}
            <div className="absolute right-0 top-0 bottom-1 w-12 pointer-events-none bg-gradient-to-l from-[#0B0F19] to-transparent" />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#0EA5E9]/30 border-t-[#0EA5E9] rounded-full animate-spin" />
            </div>
          ) : (
            <div className="px-6 pb-8">
              {/* Featured Carousel */}
              {!activeCategory && !searchQuery && featuredRecipes.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xs font-mono uppercase tracking-wider text-white/40 mb-3">Featured</h3>
                  <div className="relative">
                    <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide snap-x snap-mandatory">
                      {featuredRecipes.map(recipe => (
                        <div key={recipe.slug} className="snap-start flex-shrink-0 w-[85vw] sm:w-[45%] lg:w-[380px]">
                          <FeaturedCard recipe={recipe} onClick={() => navigate(`/recipes/${recipe.slug}`)} />
                        </div>
                      ))}
                    </div>
                    {featuredRecipes.length > 2 && (
                      <div className="absolute right-0 top-0 bottom-3 w-16 pointer-events-none bg-gradient-to-l from-[#0B0F19] to-transparent" />
                    )}
                  </div>
                </div>
              )}

              {/* Category Sections */}
              {Object.entries(groupedByCategory).map(([category, categoryRecipes]) => (
                <div key={category} className="mb-8" data-testid={`category-section-${category}`}>
                  <div className="flex items-center gap-2 mb-3">
                    {(() => { const Icon = CATEGORY_ICONS[category] || Wrench; const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS['Production Tools']; return (
                      <>
                        <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: colors.bg }}>
                          <Icon className="w-3.5 h-3.5" style={{ color: colors.text }} />
                        </div>
                        <h3 className="text-sm font-semibold text-white/80">{category}</h3>
                        <span className="text-xs text-white/30">({categoryRecipes.length})</span>
                      </>
                    );})()}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {categoryRecipes.map((recipe, idx) => (
                      <RecipeCard key={recipe.slug} recipe={recipe} index={recipes.indexOf(recipe)} onClick={() => navigate(`/recipes/${recipe.slug}`)} quickRun={runSummary[recipe.slug]} onQuickRun={() => navigate(`/recipes/${recipe.slug}?quickrun=true`)} isFavorited={favorites.has(recipe.slug)} onToggleFavorite={() => toggleFavorite(recipe.slug)} />
                    ))}
                  </div>
                </div>
              ))}

              {filteredRecipes.length === 0 && (
                <div className="text-center py-16">
                  <Search className="w-10 h-10 text-white/20 mx-auto mb-3" />
                  <p className="text-white/40 text-sm">No recipes match your search.</p>
                </div>
              )}

              {/* Request a Recipe CTA */}
              <div className="mt-8 rounded-xl p-6 text-center"
                style={{ background: 'linear-gradient(135deg, rgba(14,165,233,0.08) 0%, rgba(168,85,247,0.08) 100%)', border: '1px solid rgba(14,165,233,0.15)' }}
                data-testid="request-recipe-cta"
              >
                <h3 className="text-base font-semibold text-white mb-1">Request a Recipe</h3>
                <p className="text-xs text-white/50 mb-4">Tell us what creative workflow you need, and we'll build it.</p>
                <button
                  onClick={() => setShowRequestModal(true)}
                  className="px-5 py-2 rounded-lg text-sm font-medium text-white transition-all hover:brightness-110"
                  style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #38BDF8 100%)' }}
                  data-testid="request-recipe-btn"
                >
                  <Send className="w-3.5 h-3.5 inline mr-1.5" />
                  Submit a Request
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowRequestModal(false)}>
          <div className="w-full max-w-md rounded-xl p-6" onClick={e => e.stopPropagation()}
            style={{ background: 'rgba(19, 27, 44, 0.98)', border: '1px solid rgba(14,165,233,0.2)', backdropFilter: 'blur(20px)' }}
            data-testid="request-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-white">Request a Recipe</h3>
              <button onClick={() => setShowRequestModal(false)} className="p-1 hover:bg-white/10 rounded">
                <X className="w-4 h-4 text-white/60" />
              </button>
            </div>
            <textarea
              value={requestText}
              onChange={e => setRequestText(e.target.value)}
              placeholder="Describe the recipe you'd like us to build..."
              className="w-full h-32 px-3 py-2 rounded-lg text-sm bg-white/[0.05] border border-white/[0.08] text-white placeholder-white/30 focus:outline-none focus:border-[#0EA5E9]/40 resize-none mb-3"
              data-testid="request-description"
            />
            <input
              type="email"
              value={requestEmail}
              onChange={e => setRequestEmail(e.target.value)}
              placeholder="Email for follow-up (optional)"
              className="w-full px-3 py-2 rounded-lg text-sm bg-white/[0.05] border border-white/[0.08] text-white placeholder-white/30 focus:outline-none focus:border-[#0EA5E9]/40 mb-4"
              data-testid="request-email"
            />
            <button
              onClick={submitRequest}
              disabled={submitting || !requestText.trim()}
              className="w-full py-2.5 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #38BDF8 100%)' }}
              data-testid="request-submit"
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </div>
      )}
      <RecipeAssistant recipes={recipes} user={user} />
    </div>
  );
}


// ─── Recipe Card Component ───
function RecipeCard({ recipe, onClick, index, quickRun, onQuickRun, isFavorited, onToggleFavorite }) {
  const colors = CATEGORY_COLORS[recipe.category] || CATEGORY_COLORS['Production Tools'];
  const tierCfg = TIER_CONFIG[recipe.tier] || TIER_CONFIG['Medium'];
  const TierIcon = tierCfg.icon;

  return (
    <div
      className="text-left rounded-xl overflow-hidden transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-black/20 group relative"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
      data-testid={`recipe-card-${recipe.slug}`}
    >
      <button onClick={onClick} className="w-full text-left">
      {/* Thumbnail */}
      <div className="relative aspect-[3/2] overflow-hidden bg-black/40">
        {recipe.thumbnail_url ? (
          <img
            src={recipe.thumbnail_url}
            alt={recipe.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${colors.bg} 0%, rgba(11,15,25,0.6) 100%)` }}>
            <span className="text-3xl font-bold text-white/10">{recipe.name?.[0]}</span>
          </div>
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        {/* Category badge */}
        <div className="absolute top-2 left-2 flex items-center gap-1">
          <span className="px-2 py-0.5 rounded-full text-[9px] font-bold backdrop-blur-sm" style={{ background: `${colors.bg}cc`, color: colors.text, border: `1px solid ${colors.border}` }}>
            {recipe.category}
          </span>
          {recipe.workflow?.type === 'open' && (
            <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold backdrop-blur-sm bg-[#22C55E]/20 text-[#22C55E] border border-[#22C55E]/30">
              OPEN
            </span>
          )}
        </div>
        {/* Tier badge + number */}
        <div className="absolute top-2 right-2 flex items-center gap-1.5">
          {index != null && (
            <span className="text-[9px] font-mono text-white/25">#{index + 1}</span>
          )}
          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full backdrop-blur-sm" style={{ background: 'rgba(0,0,0,0.5)' }}>
            <TierIcon className="w-2.5 h-2.5" style={{ color: tierCfg.color }} />
            <span className="text-[9px] font-medium" style={{ color: tierCfg.color }}>{tierCfg.label}</span>
          </div>
        </div>
        {/* Bottom title overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <h4 className="text-sm font-semibold text-white drop-shadow-lg group-hover:text-[#0EA5E9] transition-colors truncate">{recipe.name}</h4>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 pt-2">
        <p className="text-[11px] text-white/50 line-clamp-2 mb-3">{recipe.description}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {recipe.creator?.verified && (
              <div className="w-4 h-4 rounded-full bg-[#0EA5E9]/20 flex items-center justify-center">
                <Sparkles className="w-2.5 h-2.5 text-[#0EA5E9]" />
              </div>
            )}
            <span className="text-[10px] text-white/40">{recipe.creator?.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-white/30">{recipe.pricing?.credits} credits</span>
            <span className="text-[10px] text-white/20">~{recipe.pricing?.estimated_seconds}s</span>
          </div>
        </div>
      </div>
      </button>

      {/* Favorite Heart — always visible when favorited, appears on hover otherwise */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
        className={`absolute top-2 right-[90px] z-10 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${
          isFavorited
            ? 'bg-rose-500/30 backdrop-blur-sm'
            : 'bg-black/40 backdrop-blur-sm opacity-0 group-hover:opacity-100'
        }`}
        title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
        data-testid={`favorite-${recipe.slug}`}
      >
        <Heart className={`w-3.5 h-3.5 transition-colors ${isFavorited ? 'text-rose-400 fill-rose-400' : 'text-white/60 hover:text-rose-400'}`} />
      </button>

      {/* Quick Run Button — only for recipes the user has run before */}
      {quickRun && (
        <button
          onClick={(e) => { e.stopPropagation(); onQuickRun(); }}
          className="absolute bottom-[52px] right-2 flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium opacity-0 group-hover:opacity-100 transition-all duration-200 z-10"
          style={{ background: 'rgba(14, 165, 233, 0.9)', color: '#fff', backdropFilter: 'blur(8px)', boxShadow: '0 2px 8px rgba(14,165,233,0.3)' }}
          title={`Re-run with last settings (${quickRun.run_count} previous run${quickRun.run_count > 1 ? 's' : ''})`}
          data-testid={`quick-run-${recipe.slug}`}
        >
          <RotateCw className="w-3 h-3" />
          Quick Run
        </button>
      )}
    </div>
  );
}


// ─── Featured Card Component ───
function FeaturedCard({ recipe, onClick }) {
  const colors = CATEGORY_COLORS[recipe.category] || CATEGORY_COLORS['Production Tools'];
  const CatIcon = CATEGORY_ICONS[recipe.category] || Wrench;

  return (
    <button
      onClick={onClick}
      className="w-full rounded-xl overflow-hidden transition-all hover:scale-[1.02] group text-left"
      style={{ border: `1px solid ${colors.border}` }}
      data-testid={`featured-card-${recipe.slug}`}
    >
      {/* Large thumbnail */}
      <div className="relative h-40 overflow-hidden bg-black/40">
        {recipe.thumbnail_url ? (
          <img
            src={recipe.thumbnail_url}
            alt={recipe.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${colors.bg} 0%, rgba(11,15,25,0.8) 100%)` }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        {/* Star badge */}
        <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 rounded-full backdrop-blur-sm" style={{ background: 'rgba(245,158,11,0.2)', border: '1px solid rgba(245,158,11,0.3)' }}>
          <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
          <span className="text-[10px] font-bold text-amber-300">Featured</span>
        </div>
      </div>
      {/* Content */}
      <div className="p-4" style={{ background: `linear-gradient(135deg, ${colors.bg} 0%, rgba(11,15,25,0.8) 100%)` }}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.3)' }}>
            <CatIcon className="w-3.5 h-3.5" style={{ color: colors.text }} />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white group-hover:text-[#0EA5E9] transition-colors">{recipe.name}</h4>
            <span className="text-[9px] text-white/40">{recipe.category}</span>
          </div>
        </div>
        <p className="text-[11px] text-white/50 line-clamp-2 mb-3">{recipe.description}</p>
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-medium" style={{ color: colors.text }}>{recipe.pricing?.credits} credits</span>
          <span className="text-[10px] text-white/30 flex items-center gap-1">
            <Clock className="w-3 h-3" /> ~{recipe.pricing?.estimated_seconds}s
          </span>
        </div>
      </div>
    </button>
  );
}
